import {
  NullableBoolean,
  NullableNumber,
  NullableString,
} from "@/code/Types";
import { Gender } from "@/enums/Gender";
import moment from "moment";
import { InForceProductGroup } from "@/enums/InForceProductGroup";
import { BaseClientDocument } from "./BaseDocuments";

export class InForceCaseData extends BaseClientDocument {
  policyNumber: NullableString = null;
  policyLastUpdated: NullableString = null;
  productGroup: NullableString = null;
  policyStatus: NullableString = null;
  productCategory: NullableString = null;
  insuredFirstName: NullableString = null;
  insuredLastName: NullableString = null;
  insuredGender: NullableString = null;
  insuredDateOfBirth: NullableString = null;
  insuredAge: NullableNumber = null;
  insuredCity: NullableString = null;
  insuredState: NullableString = null;
  insuredZip: NullableString = null;
  premiumTarget: NullableNumber = null;
  annuityType: NullableString = null;
  contractValue: NullableNumber = null;
  maturityDate: NullableString = null;
  maturityDepositGuranteedDate: NullableString = null;
  depositGuaranteedRate: NullableString = null;
  surrenderPeriod: NullableString = null;
  taxQualification: NullableString = null;
  incomeRider: NullableBoolean = null;
  totalAnnualProtectedPayment: NullableNumber = null;
  insuranceCompany: NullableString = null;
  policyType: NullableString = null;
  faceAmount: NullableNumber = null;
  annualizedPremiumAtIssue: NullableNumber = null;
  cashValue: NullableNumber = null;
  effectiveDate: NullableString = null;
  signedState: NullableString = null;
  termEndDate: NullableString = null;
  lastConvertibleDate: NullableString = null;
  owner: NullableString = null;
  beneficiary: NullableString = null;
  termEndingIn180Days: NullableBoolean = null;
  termLevelPeriodExpired: NullableBoolean = null;
  atRiskOfLapsing: NullableBoolean = null;
  laspsedLastMonth: NullableBoolean = null;
  conversionEndingIn180Days: NullableBoolean = null;
  deathClaimLastMonth: NullableBoolean = null;
  nearingRetirement: NullableBoolean = null;
  marketValueAdjustment: NullableBoolean = null;
  surrenderExpiring: NullableBoolean = null;
  surrenderExpired: NullableBoolean = null;
  requiredMinimumDistribution: NullableBoolean = null;
  productName: NullableString = null;
  agentName: NullableString = null;
  totalSurrenderValue: NullableNumber = null;
  netSurrenderValue: NullableNumber = null;

  get cashOrContractValue(): NullableNumber {
    return this.productGroup === InForceProductGroup.LIFE
      ? this.cashValue
      : this.contractValue;
  }

  get termEndOrMaturityDate(): NullableString {
    return this.productGroup === InForceProductGroup.LIFE
      ? this.termEndDate
      : this.maturityDate;
  }

  get insuredFullName(): string {
    return `${this.insuredFirstName} ${this.insuredLastName}`;
  }

  get fullInsuredGender(): string {
    if (this.insuredGender === "M") {
      return Gender.MALE;
    } else if (this.insuredGender === "F") {
      return Gender.FEMALE;
    } else {
      return "";
    }
  }

  get countEndDateYears(): number {
    return this.getYears(this.termEndDate);
  }

  get countConvertibleDateYears(): number {
    return this.getYears(this.lastConvertibleDate);
  }

  get effectiveDateReadable(): string {
    return this.getHumanReadableDate(this.effectiveDate);
  }

  get termEndDateReadable(): string {
    return this.getHumanReadableDate(this.termEndDate);
  }

  get lastConvertibleDateReadable(): string {
    return this.getHumanReadableDate(this.lastConvertibleDate);
  }

  get notificationCount(): number {
    let cnt = 0;
    if (this.productGroup === InForceProductGroup.LIFE) {
      if (this.termEndingIn180Days) {
        cnt += 1;
      }
      if (this.termLevelPeriodExpired) {
        cnt += 1;
      }
      if (this.atRiskOfLapsing) {
        cnt += 1;
      }
      if (this.laspsedLastMonth) {
        cnt += 1;
      }
      if (this.conversionEndingIn180Days) {
        cnt += 1;
      }
      if (this.deathClaimLastMonth) {
        cnt += 1;
      }
    } else if (this.productGroup === InForceProductGroup.ANNUITY) {
      if (this.surrenderExpiring) {
        cnt += 1;
      }
      if (this.surrenderExpired) {
        cnt += 1;
      }
      if (this.nearingRetirement) {
        cnt += 1;
      }
      if (this.marketValueAdjustment) {
        cnt += 1;
      }
      if (this.requiredMinimumDistribution) {
        cnt += 1;
      }
    }
    return cnt;
  }

  getYears(value: NullableString): number {
    if (value) {
      return moment(value, "MM/DD/YYYY").diff(new Date(), "years");
    }
    return 0;
  }

  getHumanReadableDate(value: NullableString): string {
    if (value) {
      try {
        const year = moment(value, "MM/DD/YYYY").diff(new Date(), "years");
        let stringToAppend = "in ";
        if (year < 0) {
          stringToAppend = "last ";
        }

        const yearsCount =
          Math.abs(moment(value, "MM/DD/YYYY").diff(new Date(), "years")) +
          " years " +
          Math.abs(
            moment(value, "MM/DD/YYYY").diff(new Date(), "months") -
              moment(value, "MM/DD/YYYY").diff(new Date(), "years") * 12,
          ) +
          " months";

        return "(" + stringToAppend + yearsCount + ")";
      } catch {
        return "";
      }
    }
    return "";
  }
}
